import React, { useState, useEffect } from "react";
import styles from "./appointments-upcoming.module.css";
import PageTitle from "../../components/screens/page-title";
import Table from "../../components/ui/table";
import Input from "../../components/ui/input";
import { useNavigate } from "react-router-dom";
import AppointmentDetailModal from "../../components/modals/appointment-detail-modal";
import { getProximasCitas } from "../../services/apiService";
import { showErrorToast } from "../../utils/notifications";
import usePageTitle from "../../hooks/usePageTitle";

const AppointmentsUpcomingPage = () => {
  usePageTitle('Próximas Citas');
  const navigate = useNavigate();
  
  // State for appointments data
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for modal
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const upcoming = await getProximasCitas();
      setUpcomingAppointments(upcoming || []);
    } catch (error) {
      console.error("Error loading appointments:", error);
      showErrorToast("Error al cargar las citas");
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    { label: "Expediente", key: "expedienteId" },
    { label: "Dirección", key: "domicilioCliente" },
    { label: "Localidad", key: "localidadCliente" },
    { label: "Fecha Inicio", key: "fechaCita" },
    { label: "Fecha Fin", key: "fechaCitaFin" },
  ];

  const handleViewDetails = (appointment, e) => {
    console.log("Viewing details for appointment:", appointment);
    
    // Verificar si es click de rueda del ratón o modificador (Ctrl/Cmd) para abrir en nueva pestaña
    try {
      const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
      const isModifier = !!(e && (e.ctrlKey || e.metaKey));
      
      if ((isMiddle || isModifier) && appointment.expedienteId) {
        window.open(`/record/${appointment.expedienteId}`, '_blank');
        return;
      }
    } catch (err) {
      console.error("Error handling click event:", err);
    }
    
    // Abrir modal normalmente si no es middle-click
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleModalUpdate = () => {
    loadAppointments();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No definida";
    const date = new Date(dateString);
    
    // Detectar si es una cita "sin hora" (00:00 o 01:00)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    if ((hours === 0 || hours === 1) && minutes === 0) {
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " - Sin hora";
    }
    
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formattedAppointments = upcomingAppointments.map(app => ({
    ...app,
    fechaCita: formatDate(app.fechaCita),
    fechaCitaFin: formatDate(app.fechaCitaFin),
  }));

  return (
    <div className={styles.container}>
      <PageTitle iconName="appointment" title="Próximas Citas" />
      
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <p>Cargando citas...</p>
          </div>
        ) : (
          <Table
            headers={headers}
            data={formattedAppointments}
            onPress={handleViewDetails}
          />
        )}
      </div>

      {isModalOpen && selectedAppointment && (
        <AppointmentDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          citaId={selectedAppointment.citaId || selectedAppointment.id || selectedAppointment.appointmentId}
          onUpdate={handleModalUpdate}
        />
      )}
    </div>
  );
};

export default AppointmentsUpcomingPage;
